import { Box } from "@material-ui/core"
import produce from "immer"
import { useMemo } from "react"
import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"
import { connect } from "react-redux"

import { FilterCategory } from ".."
import { searchChangeFilter } from "../../../redux"
import type {
  AppState,
  AppThunkDispatch,
  FilterGroup,
  LoadingState,
  SearchActions,
  SearchFilter,
  SearchFilterState,
  SearchResultState,
  SectionLoadingState
} from "../../../redux"

import "./search-result-filter.styles.scss"

export interface ISearchResultFilterStateProps {
  searchResults: SearchResultState
  filterState: SearchFilterState
  loadingState: LoadingState
  updateFilter: (filterGroup: FilterGroup, filters: string[]) => void
}

function SearchResultFilterView(props: ISearchResultFilterStateProps): ReactElement {
  const { searchResults, filterState, updateFilter, loadingState } = props
  const { t } = useTranslation("search")

  const shouldDisableFilters = useMemo<boolean>(() => {
    const state: SectionLoadingState | undefined = loadingState.search
    return state?.loading
  }, [loadingState])

  function changeFilters(filterGroup: FilterGroup): (checked: boolean, filterOption: SearchFilter) => void {
    return (checked: boolean, filterOption: SearchFilter) => {
      const newFilters: string[] = produce(filterState[filterGroup.name], (draft: string[]) => {
        if (checked) {
          if (!draft) {
            // biome-ignore lint/style/noParameterAssign: TODO in a later PR
            draft = []
          }
          draft.push(filterOption.name)
        } else {
          // biome-ignore lint/style/noParameterAssign: TODO in a later PR
          draft = draft.filter((option: string) => filterOption.name !== option)
        }
        return draft
      })
      updateFilter(filterGroup, newFilters)
    }
  }

  function getTranslatedFilterOptions(filters: SearchFilter[]): SearchFilter[] {
    return filters.map((filter) => {
      if (filter.name === "Papier en digitaal") return { ...filter, name: t("filtering.paperDigital") }
      if (filter.name === "Alleen digitaal") return { ...filter, name: t("filtering.onlyDigital") }
      return filter
    })
  }

  function getFilterTitle(filterGroupName: string): string {
    switch (filterGroupName) {
      case "Regions":
        return t("filtering.area")
      case "Categories":
        return t("filtering.category")
      case "Themes":
        return t("filtering.theme")
      case "Type":
        return t("filtering.type")
      default:
        return t("filtering.errorFilterName")
    }
  }

  return (
    <Box className="search-result-filter-container">
      {searchResults.filterGroups.map((filterGroup: FilterGroup, index: number) => (
        <FilterCategory
          // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in later PR
          key={index}
          summaryTitle={getFilterTitle(filterGroup.name)}
          filterOptions={
            filterGroup.name === "Type" ? getTranslatedFilterOptions(filterGroup.filters) : filterGroup.filters
          }
          selectedFilters={filterState[filterGroup.name]}
          onSelection={changeFilters(filterGroup)}
          disabled={shouldDisableFilters}
          radioSelection={filterGroup.name === "Type"}
        />
      ))}
    </Box>
  )
}

function mapStateToProps(state: AppState) {
  return {
    searchResults: state.search.searchResults,
    filterState: state.search.filters,
    loadingState: state.http.state
  }
}

function mapDispatchToState(dispatch: AppThunkDispatch<SearchActions>) {
  return {
    updateFilter: (filterGroup: FilterGroup, filters: string[]) => dispatch(searchChangeFilter(filterGroup, filters))
  }
}

export const SearchResultFilter = connect(mapStateToProps, mapDispatchToState)(SearchResultFilterView)
